import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import { HiddenProps } from "@insite/mobius/Hidden";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkProps } from "@insite/mobius/Link";
import { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import { PaginationProps } from "@insite/mobius/Pagination";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ShopCart from "@store/Components/Icons/ShopCart";
import { GridItemProps } from "@store/MobiusOverrides/GridItem/GridItem";
import { prevNextArrowButton, transitionHoverStyles } from "@store/theme/common/mixins";
import { cartIconStyles } from "@store/theme/common/mixins/cartIcon";
import { getLoadingOverlayProps } from "@store/theme/common/props/loadingOverlay";
import { productLineProps } from "@store/theme/common/props/productLine";
import { tableGridProps } from "@store/theme/common/props/tableGrid";
import { TYPOGRAPHY } from "@store/theme/design";
import { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import { HTMLAttributes } from "react";
import styled, { css } from "styled-components";

export interface StoreSchematicsViewStyles {
    noResults?: TypographyPresentationProps;
    carousel?: InjectableCss;
    carouselItem?: InjectableCss;
    carouselList?: InjectableCss;
    dotsWrapper?: InjectableCss;
    loadingSpinner?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    pagination?: PaginationProps;
    prevArrowButton?: ButtonPresentationProps;
    schematicLink?: LinkProps;
    schematicsWrapper?: InjectableCss;
    globalNote?: TypographyPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    container?: InjectableCss;
    tableGridHead?: GridContainerProps;
    legendHeaderGridCell?: GridItemProps;
    tableGridHeaderText?: TypographyPresentationProps;
    manufacturerNumberHeaderGridCell?: GridItemProps;
    productNumberHeaderGridCell?: GridItemProps;
    descriptionHeaderGridCell?: GridItemProps;
    wholegoodsDescriptionHeaderGridCell?: GridItemProps;
    notesHeaderGridCell?: GridItemProps;
    availabilityHeaderGridCell?: GridItemProps;
    normalPriceHeaderGridCell?: GridItemProps
    retailPriceHeaderGridCell?: GridItemProps;
    tableGridBody?: GridContainerProps;
    mobileLegendBodyGridCell?: GridItemProps;
    mobileLegendText?: TypographyPresentationProps;
    tableGridRow?: GridItemProps;
    tableGridRowExternalContainer?: GridContainerProps;
    tableGridInnerRow?: GridItemProps;
    tableGridRowContainer?: GridContainerProps;
    legendBodyGridCell?: GridItemProps;
    manufacturerNumberBodyGridCell?: GridItemProps;
    productNumberBodyGridCell?: GridItemProps;
    descriptionBodyGridCell?: GridItemProps;
    wholegoodsDescriptionBodyGridCell?: GridItemProps;
    mobileProductNumberBodyGridCell?: GridItemProps;
    mobileManufacturerNumberBodyGridCell?: GridItemProps;
    mobileHeaderHidden?: HiddenProps;
    mobileHeader?: TypographyPresentationProps;
    notesBodyGridCell?: GridItemProps;
    availabilityBodyGridCell?: GridItemProps;
    normalPriceBodyGridCell?: GridItemProps;
    productPrice?: ProductPriceStyles;
    availabilityStyles?: ProductAvailabilityStyles;
    retailPriceBodyGridCell?: GridItemProps;
    addProductBodyGridCell?: GridItemProps;
    mobileAddProductBodyGridCell?: GridItemProps;
    callBranchBodyGridCell?: GridItemProps;
    addToCartButton?: ButtonPresentationProps;
    mobileAddToCartButton?: ButtonPresentationProps;
}

interface DotButtonProps extends HTMLAttributes<HTMLButtonElement> {
    selected: boolean;
}

interface SchematicLazyImageProps extends LazyImageProps {
    selected: boolean;
    single: boolean;
}

export const DotButton = styled.button<DotButtonProps>`
    width: 10px;
    height: 10px;
    background-color: ${getColor("text.main")};
    opacity: ${props => (props.selected ? 1 : 0.5)};
    border-radius: 50%;
    cursor: pointer;
    border: none;
    padding: 0;
    margin: 0 0.5em;
`;

export const SchematicLazyImage = styled(LazyImage) <SchematicLazyImageProps>`
    display: flex;
    border: 1px solid ${getColor("common.borderDarker")};
    cursor: pointer;
    ${transitionHoverStyles("opacity")}
    ${props =>
        breakpointValuesMediaQueries(
            props.theme,
            [
                { values: props.single ? ["100%"] : ["317px", "555px", "698px", "931px"], properties: ["width"] },
                { values: ["200px", "350px", "440px", "591px"], properties: ["height"] },
            ],
            mediaOptions.min,
        )}

    ${({ single }) =>
        single
            ? css`
                  max-width: none;
              `
            : css<SchematicLazyImageProps>`
                  max-width: 931px;
                  opacity: ${({ selected }) => (selected ? 1 : 0.3)};
              `}
    img {
        width: auto;
        max-width: 100%;
        max-height: 100%;
    }

    &:hover {
        opacity: 1;
    }
` as any;

const prevNextArrowButtonStyles = css`
    position: absolute;
    top: 50%;
    ${prevNextArrowButton()}
`;

const descriptionCellStyles = css`
    ${tableGridProps?.tableGridCell?.css}
    font-size: ${TYPOGRAPHY.size.large};
    font-weight: ${TYPOGRAPHY.weight.bold};

    ${props =>
        breakpointValuesMediaQueries(
            props.theme,
            [
                { values: [null, null, null, TYPOGRAPHY.size.smaller], properties: ["font-size"] },
                { values: [null, null, null, TYPOGRAPHY.weight.base], properties: ["font-weight"] },
            ],
            mediaOptions.min,
        )}
`;

export default {
    noResults: {
        forwardAs: "p",
    },
    loadingOverlay: getLoadingOverlayProps(),
    globalNote: {
        css: css`
            display: block;
            margin-top: 0.5em;
            font-style: italic;
            font-size: ${TYPOGRAPHY.size.smaller};
            color: ${getColor("text.accent")};
        `,
    },
    loadingSpinner: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100px;
        `,
    },
    dotsWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin: 1em auto 0;
            align-items: center;
        `,
    },
    schematicLink: {
        css: css`
            display: block;
            width: 100%;
        `,
    },
    schematicsWrapper: {
        css: css`
            position: relative;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: ["1em", null, "2em"], properties: ["margin-bottom"] }],
                    mediaOptions.min,
                )}
        `,
    },
    carousel: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselList: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    carouselItem: {
        css: css`
            display: flex;
            padding: 0 0.5em;
        `,
    },
    prevArrowButton: {
        color: "common.background",
        css: css`
            ${prevNextArrowButtonStyles}
        `,
    },
    nextArrowButton: {
        color: "common.background",
        css: css`
            ${prevNextArrowButtonStyles}
            right: 0;
        `,
    },
    tableGridRowExternalContainer: {
        gap: 0,
    },
    mobileLegendBodyGridCell: {
        width: [1.75, 1, 0.75, 0, 0],
        css: css`
            justify-content: center;
        `,
    },
    mobileLegendText: {
        css: css`
            font-size: ${TYPOGRAPHY.size.large};
            font-weight: ${TYPOGRAPHY.weight.bold};
            color: ${getColor("primary")};
            margin-top: 5px;
        `,
    },
    tableGridRow: tableGridProps.tableGridRow,
    tableGridInnerRow: {
        width: [10.25, 11, 11.25, 12, 12],
    },
    tableGridHead: tableGridProps.tableGridHead,
    container: tableGridProps.container,
    tableGridBody: tableGridProps.tableGridBody,
    tableGridHeader: tableGridProps.tableGridHeader,
    tableGridRowContainer: {
        ...tableGridProps.tableGridRowContainer,
        css: css`
            ${tableGridProps.tableGridRowContainer?.css}
            ${GridOffset} {
                margin: 0;
                align-items: center;
            }
        `,
    },
    legendHeaderGridCell: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 0.5, 0.5],
    },
    legendBodyGridCell: {
        ...tableGridProps.tableGridCell,
        width: [0, 0, 0, 0.5, 0.5],
    },
    manufacturerNumberHeaderGridCell: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 1.25, 1.25],
    },
    manufacturerNumberBodyGridCell: {
        ...tableGridProps.tableGridCell,
        width: [0, 0, 0, 1.25, 1.25],
    },
    productNumberHeaderGridCell: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 1.25, 1.25],
    },
    productNumberBodyGridCell: {
        ...tableGridProps.tableGridCell,
        width: [0, 0, 0, 1.25, 1.25],
    },
    descriptionHeaderGridCell: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 2, 2],
    },
    descriptionBodyGridCell: {
        ...tableGridProps.tableGridCell,
        width: [12, 12, 12, 2, 2],
        css: css`
            ${descriptionCellStyles}
        `,
    },
    wholegoodsDescriptionHeaderGridCell: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 5.25, 5.25],
    },
    wholegoodsDescriptionBodyGridCell: {
        ...tableGridProps.tableGridCell,
        width: [12, 12, 12, 5.25, 5.25],
        css: css`
            ${descriptionCellStyles}
        `,
    },
    mobileProductNumberBodyGridCell: {
        ...tableGridProps.tableGridCell,
        width: [12, 12, 12, 0, 0],
    },
    mobileManufacturerNumberBodyGridCell: {
        ...tableGridProps.tableGridCell,
        width: [12, 12, 12, 0, 0],
    },
    notesHeaderGridCell: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 1.5, 1.5],
    },
    notesBodyGridCell: {
        ...tableGridProps.tableGridCell,
        width: [12, 12, 12, 1.5, 1.5],
        css: css`
            flex-direction: column;
            align-items: flex-start;
        `,
    },
    availabilityHeaderGridCell: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 1.75, 1.75],
    },
    availabilityBodyGridCell: {
        ...tableGridProps.tableGridCell,
        width: [0, 0, 0, 1.75, 1.75],
    },
    normalPriceHeaderGridCell: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 1.25, 1.25],
    },
    normalPriceBodyGridCell: {
        ...tableGridProps.tableGridCell,
        width: [6, 4, 4, 1.25, 1.25],
        css: css`
            font-size: ${TYPOGRAPHY.size.large};
            font-weight: ${TYPOGRAPHY.weight.bold};
            flex-direction: column;
            align-items: flex-start;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        { values: [null, null, null, TYPOGRAPHY.size.smaller], properties: ["font-size"] },
                        { values: [null, null, null, TYPOGRAPHY.weight.base], properties: ["font-weight"] },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    productPrice: {
        ...productLineProps.price,
        price: {
            ...productLineProps.price?.price,
            priceText: {
                ...productLineProps.price?.price?.priceText,
                weight: TYPOGRAPHY.weight.bold,
                size: TYPOGRAPHY.size.smaller
            },
        },
        retailPrice: {
            ...productLineProps.price?.retailPrice,
            weight: TYPOGRAPHY.weight.bold,
            size: TYPOGRAPHY.size.smaller,
            css: css`
                color: ${getColor("text")}
            `,
        },
    },
    availabilityStyles: {
        availabilityWrapper: {
            css: css`
                margin-bottom: 0px;
            `,
        },
    },
    retailPriceHeaderGridCell: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 2.5, 2.5],
    },
    retailPriceBodyGridCell: {
        ...tableGridProps.tableGridCell,
        width: [6, 8, 8, 1.25, 1.25],
        css: css`
            align-items: flex-end;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, null, null, "flex-start"], properties: ["align-items"] }],
                    mediaOptions.min,
                )}
        `,
    },
    addProductBodyGridCell: {
        ...tableGridProps.tableGridCell,
        width: [0, 0, 0, 1.25, 1.25],
        css: css`
            ${tableGridProps?.tableGridCell?.css}
            justify-content: right;
        `,
    },
    mobileAddProductBodyGridCell: {
        ...tableGridProps.tableGridCell,
        width: [12, 12, 12, 0, 0],
    },
    callBranchBodyGridCell: {
        ...tableGridProps.tableGridCell,
        width: [12, 12, 12, 1.25, 1.25],
        css: css`
            ${tableGridProps?.tableGridCell?.css}
            justify-content: left;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, null, null, "right"], properties: ["justify-content"] }],
                    mediaOptions.min,
                )}
        `,
    },
    tableGridHeaderText: tableGridProps.textHeader,
    mobileHeaderHidden: tableGridProps.mobileHeaderHidden,
    mobileHeader: tableGridProps.mobileHeader,
    addToCartButton: {
        icon: {
            position: "right",
            src: ShopCart,
        },
        css: css`
            ${cartIconStyles}
        `,
    },
    mobileAddToCartButton: {
        css: css`
            width: 100%;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, "50%", "40%", null, null], properties: ["width"] }],
                    mediaOptions.min,
                )}
        `,
    },
} as StoreSchematicsViewStyles;